import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Typography, Modal, TextField, Divider, FormControl, Select, MenuItem, IconButton, OutlinedInput, FormHelperText } from '@mui/material';
import { Add as AddIcon, VisibilityOff, Visibility, Close as CloseIcon } from '@mui/icons-material';
import Sheet from '@mui/joy/Sheet';
import ModalClose from '@mui/joy/ModalClose';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserData } from '../../../Helpers/userSlice';
import { InputAdornment } from "@mui/material";
import NCRFormHeading from '../../Utilities/NCRFormHeading';
import PrimaryContent from '../../Utilities/PrimaryContent';
import NCRTable from '../../Utilities/NCRTable';
import CircularLoader from '../../Common/CircularLoader';
import ExportButton from '../../Utilities/ExportButton';

const AddUserLabel = ({ sx, children }) => {
  return (
    <Typography
      className='add-user-label'
      variant='h2'
      sx={{
        fontFamily: 'Lato, sans-serif',
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '16.8px',
        color: '#000000',
        mb: 1,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

export default function UserManagement() {
  const [open, setOpen] = React.useState(false);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [allUsersData, setAllUsersData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = React.useState(false);
  const [errors, setErrors] = useState({ clientId: false, userName: false, password: false });
  const [validationErrors, setValidationErrors] = useState({ message: false, expiry: false });
  const [editingUser, setEditingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [exportUser, setExportUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [activeAnnouncement, setActiveAnnouncement] = useState(false);

  const accessToken = useMemo(() => localStorage.getItem('NCProAccessToken'), []);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const [formData, setFormData] = useState({
    clientId: '',
    fullname: '',
    userName: '',
    email: '',
    password: '',
    roleCode: '',
  });

  const [announcement, setAnnouncement] = useState({
    message: '',
    expiry: ''
  })

  const handleOpenAddUser = () => {
    setEditingUser(null);
    setFormData({
      clientId: userData.roleCode === "CLTADM" ? userData.clientId : "",
      fullname: userData.roleCode === "CLTADM" ? userData.fullName : "",
      userName: "",
      email: "",
      password: "",
      roleCode: userData.roleCode === "SUPADM" ? "CLTADM" : "CLTUSR",
    });
    setOpen(true);
  };

  const handleOpenAnnouncement = () => {
    setAnnouncementOpen(true)
    setAnnouncement({
      message: "",
      expiry: ""
    })
    setValidationErrors({
      message: false,
      expiry: false
    })
  }

  const clearAnnouncement = async () => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/announcement/update/${announcements?.data?.[0]?.notification_id}`,
        { announcement_is_active: false },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (response) {
        setClearOpen(false);
        setActiveAnnouncement(false)
      }
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error clearing announcement:', error.response);
      toast.error(error.response?.data?.message || "Failed to clear announcement");
    }
  }

  const getUsersData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/get-users`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setAllUsersData(response.data.users);
      setUsersData(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportClick = () => {
    setExportUser(true);
    setTimeout(() => {
      setExportUser(false);
    }, 100);
  };

  const getAnnouncement = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/announcement/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = response.data;
      if (data) {
        setAnnouncements(data);
        setActiveAnnouncement(true);
      }
      else {
        setActiveAnnouncement(false)
      }

    } catch (error) {
      console.log(error.response.data.message);
      setActiveAnnouncement(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      await getUsersData();
    };

    if (!allUsersData.length) {
      fetchUsers();
    }

    if (!searchTerm) {
      setUsersData(allUsersData);
    } else {
      const filtered = allUsersData.filter((user) =>
        Object.values(user).some(
          (value) =>
            typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setUsersData(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const { userData, isAuthenticated } = useSelector((state) => state.user);

  useEffect(() => {
    getAnnouncement(userData.userAutoGeneratedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAnnouncement, userData.userAutoGeneratedId, accessToken]);

  useEffect(() => {
    if (!isAuthenticated && accessToken) {
      dispatch(fetchUserData(accessToken));
    } else if (!accessToken) {
      navigate('/login');
    }

    if (userData.clientId && userData.roleCode === 'CLTADM') {
      setFormData((prevData) => ({
        ...prevData,
        clientId: userData.clientId,
      }));
    } else if (userData.fullName && userData.roleCode === 'CLTADM') {
      setFormData((prevData) => ({
        ...prevData,
        fullname: userData.fullName,
      }));
    }
    else {
      setFormData((prevData) => ({
        ...prevData,
        clientId: '',
      }));
    }
    setFormData((prevData) => ({
      ...prevData,
      roleCode: userData.roleCode === 'SUPADM' ? 'CLTADM' : 'CLTUSR',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, isAuthenticated, dispatch, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'userName' || name === 'clientId') {
      const isValid = /^[a-z0-9]+$/.test(value);
      if (!isValid && value !== '') {
        toast.error("Special characters or capital letters are not allowed.");
        return;
      };
    }
    setFormData((prevData) => ({ ...prevData, [name]: name === 'clientId' ? value : value }));

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const handleAnnouncementChange = (e) => {
    const { name, value } = e.target;
    setAnnouncement((prev) => ({
      ...prev,
      [name]: value
    }))

    setValidationErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  }

  const handleEditClick = (user) => {
    setFormData({
      clientId: user.clientId,
      fullname: user.fullname,
      userName: user.username,
      email: user.email,
      password: '',
      roleCode: user.roleCode,
    });
    setEditingUser(user);
    setOpen(true);
    setErrors({ clientId: false, userName: false, password: false });
  };

  const handleDeleteUser = async (user) => {

    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;  //Later, it will change to a popup view
    }

    try {
      setIsLoading(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/delete/${user?.user_auto_generate_id}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.data.success) {
        toast.success("User deleted successfully");
        await getUsersData();
      } else {
        toast.error(response.data.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error.response || error.message);
      toast.error(error.response?.data?.message || "Failed to delete user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const { clientId, fullname, userName, email, password, roleCode } = formData;

      let newErrors = {};
      if (!clientId && !editingUser) newErrors.clientId = true;
      if (!userName) newErrors.userName = true;
      if (!fullname) newErrors.fullname = true;
      if (!email) newErrors.email = true;
      if (!password && !editingUser) newErrors.password = true;
      if (!roleCode && !editingUser) newErrors.roleCode = true;

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error("Please fill in all required fields.");
        return;
      }

      let requestData = { fullname, userName, email };
      if (!editingUser) {
        requestData.clientId = clientId;
        requestData.password = password;
        requestData.roleCode = roleCode;
      } else if (userData.roleCode === "SUPADM") {
        if (clientId) requestData.clientId = clientId;
        if (roleCode) requestData.roleCode = roleCode;
      }

      let response;
      if (editingUser) {
        response = await axios.put(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/user/edit/${editingUser.user_auto_generate_id}`,
          requestData,
          { headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` } }
        );
        toast.success("User updated successfully");
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/user/register`,
          requestData,
          { headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` } }
        );
        toast.success("User created successfully");
      }

      if (response.data.success) {
        setOpen(false);
        await getUsersData();
        setFormData({
          clientId: userData.roleCode === 'CLTADM' ? userData.clientId : '',
          fullname: userData.roleCode === 'CLTADM' ? userData.fullName : '',
          userName: '',
          email: '',
          password: '',
          roleCode: userData.roleCode === 'SUPADM' ? 'CLTADM' : 'CLTUSR',
        });
        setEditingUser(null);
      }
    } catch (error) {
      console.error("Error updating user:", error.response || error.message);
      toast.error(error.response?.data?.message || "Failed to update user");
    }
  };

  const submitAnnouncement = async () => {
    try {
      const { message, expiry } = announcement;

      let newErrors = {};
      if (!message)
        newErrors.message = true;
      if (!expiry)
        newErrors.expiry = true;

      console.log(message, expiry);
      if (Object.keys(newErrors).length > 0) {
        setValidationErrors(newErrors);
        toast.error("Please fill in all required fields.");
        return;
      }

      let data = { message, days: Number(expiry) };
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/announcement/create`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`
          }
        }
      )

      if (response.data.status) {
        toast.success("Announcement created successfully!");
        setAnnouncementOpen(false);
        setActiveAnnouncement(true)
      }
      else if (response.data.status === false) {
        toast.error(response.data.message)
      }

    } catch (error) {
      console.error("Error submitting announcement", error.response);
      toast.error(error.response?.data?.message);
    }
  }

  return (
    <>
      <Box sx={{ pt: 12, px: 1 }}>
        <Box sx={{ pt: 2, }}>
          <NCRFormHeading
            sx={{ fontSize: '18px', pt: 0.4 }}
            tooltipData={
              [
                { description: "Manage your user accounts by creating, editing, and modifying user details to ensure proper access and control." },
              ]
            }
          >User management</NCRFormHeading>
        </Box>
        <div style={{ paddingBottom: '18px', display: 'flex', alignItems: 'center' }}>
          {allUsersData.length > 0 &&
            <div>
              <TextField
                variant="outlined"
                placeholder="Search any keyword"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  maxWidth: 340,
                  minWidth: 240,
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "& .MuiInputBase-input": {
                    fontFamily: 'Lato, sans-serif',
                    fontSize: "14px",
                    fontWeight: 400,
                    color: '#545454',
                    mt: 0.3,
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <img
                          src="/images/search-icon.png"
                          alt="Search Icon"
                          style={{
                            width: '16px',
                            height: '16px',
                            objectFit: "contain",
                          }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>
          }
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 7 }}>
            {userData.roleCode === 'SUPADM' &&
              <>
                {!loading && activeAnnouncement && <IconButton
                  onClick={() => setClearOpen(true)}
                  size='small'
                  sx={{
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>}
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={handleOpenAnnouncement}
                  disabled={activeAnnouncement}
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                    textTransform: 'none',
                    color: 'white',
                    borderRadius: '10px',
                    py: 1,
                    "&.Mui-disabled": {
                      background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                      color: 'white',
                      opacity: '70%',
                      border: 'none'
                    },
                  }}
                >Announcement</Button>
              </>}
            {usersData.length > 0 && <ExportButton onClick={handleExportClick} />}
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              color="neutral"
              onClick={handleOpenAddUser}
              disabled={(userData.roleCode === "CLTADM" && usersData.length >= 3)}
              sx={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                textTransform: 'none',
                color: 'white',
                borderRadius: '10px',
                opacity: (userData.roleCode === "CLTADM" && usersData.length >= 3) ? '70%' : '100%',
                py: 1,
                "&.Mui-disabled": {
                  background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                  color: 'white',
                  opacity: '70%',
                  border: 'none'
                },
              }}
            >
              Add User
            </Button>
          </div>
        </div>
        {isLoading ? (
          <CircularLoader />
        ) : usersData.length === 0 ? (
          <PrimaryContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "350px",
              textAlign: "center",
            }}
          >
            No records found
          </PrimaryContent>
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <NCRTable
              data={{ formData: usersData || [], page: "users", userData: userData }}
              onEdit={handleEditClick}
              onDelete={handleDeleteUser}
              onExport={exportUser}
            />
          </Box>
        )}
        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={open}
          onClose={() => setOpen(false)}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Sheet
            variant="outlined"
            sx={{ maxWidth: 500, borderRadius: 'md', boxShadow: 'lg' }}
          >
            <ModalClose size='lg' variant="plain" onClick={() => setOpen(false)} />
            <Typography component="h2" sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '16px',
              fontWeight: '700',
              color: '#000000',
              px: 3,
              py: 0.8,
              mt: 1
            }}>
              {editingUser ? 'Edit User' : 'Add User'}
            </Typography>
            <Divider sx={{ mb: 2, mt: 1 }} />
            <Box sx={{ display: 'flex', mb: 2, px: 3 }}>
              <Box sx={{ pr: 5, flex: 1 }}>
                <AddUserLabel>Client ID</AddUserLabel>
                <TextField
                  className='add-user-textfield'
                  name="clientId"
                  size="small"
                  placeholder="Enter Client ID"
                  fullWidth
                  value={formData.clientId}
                  disabled={userData.roleCode === 'CLTADM' ? true : false || !!editingUser}
                  onChange={handleChange}
                  error={errors.clientId}
                  helperText={errors.clientId ? "Client ID is required." : ""}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <AddUserLabel>User Name</AddUserLabel>
                <TextField
                  name="userName"
                  size="small"
                  placeholder="Enter user name"
                  fullWidth
                  value={formData.userName}
                  onChange={handleChange}
                  error={errors.userName}
                  helperText={errors.userName ? "User Name is required." : ""}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', mb: 2, px: 3 }}>
              <Box sx={{ pr: 5, flex: 1 }}>
                <AddUserLabel>Client name</AddUserLabel>
                <TextField
                  name="fullname"
                  size="small"
                  placeholder="Enter Client Name"
                  fullWidth
                  value={formData.fullname}
                  onChange={handleChange}
                  disabled={userData.roleCode === 'CLTADM' ? true : false || !!editingUser}
                  error={errors.fullname}
                  helperText={errors.fullname ? "Client Name is required." : ""}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <AddUserLabel>Email address</AddUserLabel>
                <TextField
                  name="email"
                  size="small"
                  placeholder="Enter email address"
                  fullWidth
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  helperText={errors.email ? "Email is required." : ""}
                  inputProps={{
                    readOnly: true,
                    onFocus: (e) => e.target.removeAttribute("readonly"),
                    autocomplete: "off",
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', mb: 2, px: 3 }}>
              <Box sx={{ pr: 5, flex: 1 }}>
                <AddUserLabel>Set password</AddUserLabel>
                <FormControl fullWidth error={errors.password} size="small">
                  <OutlinedInput
                    id="outlined-adornment-password"
                    size="small"
                    className='add-user-textfield'
                    name="password"
                    value={editingUser ? "*****" : formData.password}
                    disabled={!!editingUser}
                    onChange={handleChange}
                    type={showPassword || editingUser ? 'text' : 'password'}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          onMouseUp={handleMouseUpPassword}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                    placeholder="Set password"
                  />
                  {errors.password && (
                    <FormHelperText>Password is required.</FormHelperText>
                  )}
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <AddUserLabel>Role</AddUserLabel>
                <FormControl variant="outlined">
                  <Select
                    sx={{
                      width: '200px', height: '42px',
                    }}
                    disabled={userData.roleCode === "CLTADM" || !!editingUser}
                    size="small"
                    name="roleCode"
                    className='add-user-textfield'
                    value={formData.roleCode || ""}
                    displayEmpty
                    onChange={handleChange}
                    error={errors.roleCode}
                    renderValue={(selected) => {
                      if (selected) return selected;
                      if (userData.roleCode === "CLTADM") return "CLTUSR";
                      if (userData.roleCode === "SUPADM") return "CLTADM";
                      if (editingUser && formData.roleCode) return formData.roleCode;
                      return "Select option";
                    }}
                    inputProps={{
                      "aria-label": "Without label",
                    }}
                    MenuProps={{
                      disablePortal: true,
                    }}
                  >
                    <MenuItem key={0} value="CLTADM" className='dropdown-menu-item'>
                      CLTADM
                    </MenuItem>
                    <MenuItem key={1} value="CLTUSR" className='dropdown-menu-item'>
                      CLTUSR
                    </MenuItem>
                  </Select>
                  {errors.roleCode && <FormHelperText error>Role is required</FormHelperText>}
                </FormControl>
              </Box>
            </Box>
            <Box sx={{
              borderRadius: '0px 0px 10px 10px',
              boxShadow: '0px -1px 4px 0px #00000026',
              textAlign: 'right',
              px: 3,
              py: 1.5,
              mt: 3,
            }}>
              <Button
                variant="outlined"
                color="neutral"
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '14px',
                  fontWeight: '700',
                  border: '1px solid #0E2966 !important',
                  textTransform: 'none',
                  borderRadius: '7px',
                  color: '#0E2966 !important',
                  mr: 2
                }}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '7px',
                  px: 2,
                }}
              >
                {editingUser ? 'Save' : 'Create'}
              </Button>
            </Box>
          </Sheet>
        </Modal>

        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={announcementOpen}
          onClose={() => setAnnouncementOpen(false)}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Sheet
            variant="outlined"
            sx={{ width: "600px", borderRadius: 'md', boxShadow: 'lg' }}
          >
            <ModalClose size='lg' variant="plain" onClick={() => setAnnouncementOpen(false)} />
            <Typography component="h2" sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '16px',
              fontWeight: '700',
              color: '#000000',
              px: 3,
              py: 0.8,
              mt: 1
            }}>
              Announcement
            </Typography>
            <Divider sx={{ mb: 2, mt: 1 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ px: 2 }}>
                <AddUserLabel>Message</AddUserLabel>
                <TextField
                  className='add-user-textfield'
                  name="message"
                  size="small"
                  placeholder="Enter message"
                  fullWidth
                  value={announcement.message}
                  onChange={handleAnnouncementChange}
                  error={validationErrors.message}
                  helperText={validationErrors.message ? "Message is required" : ""}
                  multiline
                  minRows={3}
                  maxRows={3}
                  inputProps={{ maxLength: 400 }}
                />
              </Box>
              <Box sx={{ px: 2 }}>
                <AddUserLabel>Expiry Days</AddUserLabel>
                <TextField
                  className='add-user-textfield'
                  name="expiry"
                  size="small"
                  type="number"
                  placeholder="Enter expiry days"
                  fullWidth
                  value={announcement.expiry}
                  onChange={handleAnnouncementChange}
                  error={validationErrors.expiry}
                  helperText={validationErrors.expiry ? "Expiry days is required" : ""}
                  inputProps={{ min: 1 }}
                  onKeyDown={(e) => {
                    const invalidChars = ['-', '+', '.', 'e', 'E'];
                    if (invalidChars.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Box>
            </Box>
            <Box sx={{
              borderRadius: '0px 0px 10px 10px',
              boxShadow: '0px -1px 4px 0px #00000026',
              textAlign: 'right',
              px: 3,
              py: 1.5,
              mt: 3,
            }}>
              <Button
                variant="outlined"
                color="neutral"
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '14px',
                  fontWeight: '700',
                  border: '1px solid #0E2966 !important',
                  textTransform: 'none',
                  borderRadius: '7px',
                  color: '#0E2966 !important',
                  mr: 2
                }}
                onClick={() => setAnnouncementOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={submitAnnouncement}
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '7px',
                  px: 2,
                }}
              >
                Submit
              </Button>
            </Box>
          </Sheet>
        </Modal>

        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={clearOpen}
          onClose={() => setClearOpen(false)}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Sheet
            variant="outlined"
            sx={{ width: "600px", borderRadius: 'md', boxShadow: 'lg' }}
          >
            <ModalClose size='lg' variant="plain" onClick={() => setClearOpen(false)} />
            <Typography component="h2" sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '16px',
              fontWeight: '700',
              color: '#000000',
              px: 2,
              py: 0.8,
              mt: 1
            }}>
              Delete Confirmation
            </Typography>
            <Divider sx={{ mt: 1 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ px: 2 }}>
                <Typography component="h2" sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '16px',
                  color: '#000000',
                  // px: 2,
                  py: 0.8,
                  mt: 1
                }}>
                  Are you sure to clear the following announcement?
                </Typography>
                <Typography component="h2" sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '16px',
                  color: '#000000',
                  py: 0.8,
                  mt: 1,
                  wordBreak: 'break-word'
                }}>
                  {announcements?.data?.[0]?.message}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              borderRadius: '0px 0px 10px 10px',
              boxShadow: '0px -1px 4px 0px #00000026',
              textAlign: 'right',
              px: 3,
              py: 1.5,
              mt: 3,
            }}>
              <Button
                variant="outlined"
                color="neutral"
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '14px',
                  fontWeight: '700',
                  border: '1px solid #0E2966 !important',
                  textTransform: 'none',
                  borderRadius: '7px',
                  color: '#0E2966 !important',
                  mr: 2
                }}
                onClick={() => setClearOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={clearAnnouncement}
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '7px',
                  px: 2,
                }}
              >
                Delete
              </Button>
            </Box>
          </Sheet>
        </Modal>
      </Box>
    </>
  );
}
